import { useState, useEffect, useCallback } from "react";
import { withTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';

import api from "../../../api/api";
import countriesData from "../../Config/countries.json";
import {
    fetchChats,
    fetchHistory,
    selectChat,
    sendMessage
} from "./actions";

import WhatsAppSidebar from "./components/WhatsAppSidebar";
import WhatsAppChatWindow from "./components/WhatsAppChatWindow";
import WhatsAppModals from "./components/WhatsAppModals";
import "./WhatsApp.css";

const VALID_COUNTRY_CODES = new Set(countriesData.countries.map(c => c.value.toLowerCase()));

const countryPrefixes = {};
countriesData.countries.forEach(c => {
    if (c.phoneCode && c.value) {
        countryPrefixes[c.phoneCode] = c.value.toLowerCase();
    }
});
countryPrefixes["1"] = "us";


const WhatsApp = ({ t, match, history }) => {
    const dispatch = useDispatch();

    // Redux selectors
    const {
        chats,
        activeChat,
        messages,
        loadingChats,
        loadingHistory,
        sendingMessage
    } = useSelector(state => state.whatsapp || {});

    // Local state for UI states
    const [newMessage, setNewMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [modalPhone, setModalPhone] = useState("");
    const [modalCountry, setModalCountry] = useState("il");
    const [modalFirstName, setModalFirstName] = useState("");
    const [modalLastName, setModalLastName] = useState("");
    const [checkingPhone, setCheckingPhone] = useState(false);
    const [checkResult, setCheckResult] = useState(null);
    const [showUserInfoModal, setShowUserInfoModal] = useState(false);
    const [userInfoData, setUserInfoData] = useState(null);
    const [loadingUserInfo, setLoadingUserInfo] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const handleSelectChat = (chat) => {
        const phone = chat.id.split('@')[0];
        if (history) {
            history.push(`/ControlPanel/WhatsApp/${phone}`);
        }
        dispatch(selectChat(chat));
    };

    // Initial load
    useEffect(() => {
        dispatch(fetchChats());
    }, [dispatch]);

    const phoneParam = match?.params?.phone;

    // URL phone parameter sync to select active chat
    useEffect(() => {
        if (!loadingChats && chats) {
            if (phoneParam) {
                const cleanParam = phoneParam.replace(/\D/g, '');
                if (cleanParam) {
                    const activePhone = activeChat?.id?.split('@')[0];
                    if (activePhone !== cleanParam) {
                        const matchedChat = chats.find(c => c.id.split('@')[0] === cleanParam);
                        if (matchedChat) {
                            dispatch(selectChat(matchedChat));
                        } else {
                            dispatch(selectChat({
                                id: `${cleanParam}@c.us`,
                                name: phoneParam,
                                avatar: null,
                                image: null,
                                lastMessage: null
                            }));
                        }
                    }
                }
            } else if (activeChat) {
                // If we navigate to the base path, clear the active chat selection
                dispatch({ type: 'WHATSAPP_SELECT_CHAT', chat: null });
            }
        }
    }, [phoneParam, chats, loadingChats, activeChat, dispatch]);

    // Polling active chat message history every 10 seconds
    useEffect(() => {
        let pollInterval;
        if (activeChat) {
            pollInterval = setInterval(() => {
                dispatch(fetchHistory(activeChat.id, false));
            }, 10000);
        }
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [activeChat, dispatch]);

    // Country flags mapping
    const getCountryFlag = useCallback((countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.icon : `/assets/flags/32x32/${code}.png`;
    }, []);

    const getPhotoUrl = useCallback((photo) => {
        if (!photo) return null;
        if (photo.startsWith("http://") || photo.startsWith("https://") || photo.startsWith("data:")) {
            return photo;
        }
        if (photo.startsWith("/assets/")) {
            return photo;
        }
        const baseUrl = process.env.REACT_APP_BASE_URL || "";
        const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
        const cleanPhoto = photo.startsWith("/") ? photo : "/" + photo;
        return cleanBaseUrl + cleanPhoto;
    }, []);

    // Resolving country from phone code via countries.json validation
    const getCountryFromWhatsAppId = useCallback((chatId) => {
        if (!chatId) return null;
        const phone = chatId.split('@')[0];

        const prefix = Object.keys(countryPrefixes)
            .sort((a, b) => b.length - a.length)
            .find(pref => phone.startsWith(pref));
        if (prefix) {
            const countryCode = countryPrefixes[prefix];
            return VALID_COUNTRY_CODES.has(countryCode) ? countryCode : null;
        }
        return null;
    }, []);

    // Operations
    const handleCheckWhatsApp = () => {
        setCheckingPhone(true);
        setCheckResult(null);
        api.get(`/green/check?phone=${modalPhone}&country=${modalCountry}`)
            .then(response => {
                setCheckingPhone(false);
                setCheckResult(response.data.exists);
            })
            .catch(error => {
                setCheckingPhone(false);
                setCheckResult(false);
                toast.error(t("Pages.WhatsApp.errorOccurred"));
            });
    };

    const handleAddContactSubmit = (e) => {
        e.preventDefault();
        api.post('/green/contact/add', {
            phone: modalPhone,
            country: modalCountry,
            firstName: modalFirstName,
            lastName: modalLastName
        }, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => {
                if (response.data && response.data.success) {
                    toast.success(t("Pages.WhatsApp.successAdd"));
                    setShowAddModal(false);
                    setModalPhone("");
                    setModalFirstName("");
                    setModalLastName("");
                    setCheckResult(null);
                    dispatch(fetchChats());
                } else {
                    toast.error(t("Pages.WhatsApp.errorOccurred"));
                }
            })
            .catch(error => {
                toast.error(t("Pages.WhatsApp.errorOccurred"));
            });
    };

    const handleEditContactSubmit = (e) => {
        e.preventDefault();
        api.post('/green/contact/edit', {
            chatId: activeChat.id,
            firstName: modalFirstName,
            lastName: modalLastName
        }, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => {
                if (response.data && response.data.success) {
                    toast.success(t("Pages.WhatsApp.successEdit"));
                    setShowEditModal(false);
                    setModalFirstName("");
                    setModalLastName("");

                    const updatedChat = { ...activeChat, name: modalFirstName.trim() + " " + modalLastName.trim() };
                    dispatch({ type: 'WHATSAPP_SELECT_CHAT', chat: updatedChat });
                    dispatch(fetchChats());
                } else {
                    toast.error(t("Pages.WhatsApp.errorOccurred"));
                }
            })
            .catch(error => {
                toast.error(t("Pages.WhatsApp.errorOccurred"));
            });
    };

    const handleDeleteContact = () => {
        if (window.confirm(t("Pages.WhatsApp.deleteContactConfirm"))) {
            api.post('/green/contact/delete', {
                chatId: activeChat.id
            }, {
                headers: { 'Content-Type': 'application/json' }
            })
                .then(response => {
                    if (response.data && response.data.success) {
                        toast.success(t("Pages.WhatsApp.successDelete"));
                        dispatch({ type: 'WHATSAPP_SELECT_CHAT', chat: null });
                        dispatch(fetchChats());
                    } else {
                        toast.error(t("Pages.WhatsApp.errorOccurred"));
                    }
                })
                .catch(error => {
                    toast.error(t("Pages.WhatsApp.errorOccurred"));
                });
        }
    };

    const handleShowUserInfo = (chat = null) => {
        const targetChat = chat || activeChat;
        if (!targetChat) return;

        setShowUserInfoModal(true);
        setLoadingUserInfo(true);
        setUserInfoData(null);
        api.get(`/green/user-info?chatId=${targetChat.id}`)
            .then(response => {
                const info = {
                    ...response.data,
                    photo: response.data.photo || targetChat.avatar || targetChat.image || null
                };
                setUserInfoData(info);
                setLoadingUserInfo(false);

                // Cache info on chat object in Redux to dynamically render registration badges
                const updatedChats = chats.map(c => {
                    if (c.id === targetChat.id) {
                        return { ...c, systemUser: info };
                    }
                    return c;
                });
                dispatch({ type: 'WHATSAPP_UPDATE_CHATS', chats: updatedChats });
            })
            .catch(error => {
                setUserInfoData({
                    isWhatsAppOnly: true,
                    firstName: targetChat.name || targetChat.id.split('@')[0],
                    lastName: "",
                    phone: targetChat.id.split('@')[0],
                    id: targetChat.id,
                    photo: targetChat.avatar || targetChat.image || null,
                    country: targetChat.systemUser && targetChat.systemUser.country ? targetChat.systemUser.country : getCountryFromWhatsAppId(targetChat.id)
                });
                setLoadingUserInfo(false);
            });
    };

    return (
        <>
            <Helmet>
                <title>{t("Pages.WhatsApp.title")} | {t("alarabeyya")}</title>
            </Helmet>

            <div className="whatsapp-page-wrapper">
                {!sidebarOpen && (
                    <div
                        className="mobile-sidebar-toggle-btn d-md-none"
                        onClick={() => setSidebarOpen(true)}
                        title={t("Pages.Chatbot.OpenMenu") || "فتح القائمة"}
                    >
                        <i className="fal fa-bars"></i>
                    </div>
                )}
                <div className="whatsapp-container">
                    <WhatsAppSidebar
                        chats={chats}
                        activeChat={activeChat}
                        loadingChats={loadingChats}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                        onAddContactClick={() => {
                            setShowAddModal(true);
                            setModalPhone("");
                            setModalFirstName("");
                            setModalLastName("");
                            setCheckResult(null);
                        }}
                        onSyncClick={() => dispatch(fetchChats(true))}
                        onSelectChat={handleSelectChat}
                        onShowUserInfo={handleShowUserInfo}
                        getPhotoUrl={getPhotoUrl}
                        getCountryFlag={getCountryFlag}
                        getCountryFromWhatsAppId={getCountryFromWhatsAppId}
                        t={t}
                    />

                    {activeChat ? (
                        <WhatsAppChatWindow
                            activeChat={activeChat}
                            messages={messages}
                            loadingHistory={loadingHistory}
                            sendingMessage={sendingMessage}
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            onSendMessage={(e) => {
                                e.preventDefault();
                                if (newMessage.trim()) {
                                    dispatch(sendMessage(activeChat.id, newMessage.trim(), t));
                                    setNewMessage("");
                                }
                            }}
                            onShowUserInfo={handleShowUserInfo}
                            onEditContactClick={(fName, lName) => {
                                setShowEditModal(true);
                                setModalFirstName(fName);
                                setModalLastName(lName);
                            }}
                            onDeleteContactClick={handleDeleteContact}
                            onBackToList={() => {
                                if (history) {
                                    history.push('/ControlPanel/WhatsApp');
                                }
                            }}
                            getPhotoUrl={getPhotoUrl}
                            getCountryFlag={getCountryFlag}
                            getCountryFromWhatsAppId={getCountryFromWhatsAppId}
                            t={t}
                        />
                    ) : (
                        <div className="whatsapp-welcome-screen">
                            <div className="whatsapp-welcome-content">
                                <i className="fab fa-whatsapp whatsapp-welcome-icon"></i>
                                <h4 className="mt-3">{t("Pages.WhatsApp.welcomeTitle")}</h4>
                                <p className="text-muted mt-2">{t("Pages.WhatsApp.welcomeText")}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <WhatsAppModals
                showAddModal={showAddModal}
                setShowAddModal={setShowAddModal}
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                showUserInfoModal={showUserInfoModal}
                setShowUserInfoModal={setShowUserInfoModal}
                activeChat={activeChat}
                userInfoData={userInfoData}
                setUserInfoData={setUserInfoData}
                loadingUserInfo={loadingUserInfo}
                checkingPhone={checkingPhone}
                setCheckingPhone={setCheckingPhone}
                checkResult={checkResult}
                setCheckResult={setCheckResult}
                modalPhone={modalPhone}
                setModalPhone={setModalPhone}
                modalCountry={modalCountry}
                setModalCountry={setModalCountry}
                modalFirstName={modalFirstName}
                setModalFirstName={setModalFirstName}
                modalLastName={modalLastName}
                setModalLastName={setModalLastName}
                handleCheckWhatsApp={handleCheckWhatsApp}
                handleAddContactSubmit={handleAddContactSubmit}
                handleEditContactSubmit={handleEditContactSubmit}
                getPhotoUrl={getPhotoUrl}
                getCountryFlag={getCountryFlag}
                getCountryFromWhatsAppId={getCountryFromWhatsAppId}
                fetchChats={() => dispatch(fetchChats())}
                t={t}
            />
        </>
    );
};

export default withTranslation('common')(WhatsApp);
